import { useEffect, useState } from "react";
import { FileCheck2, FileText, Loader2, ShieldAlert, ScrollText, Trash2, Info, Clock, Download, Lock } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Kennisbank";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ACCENT = { chip: "bg-teal-600", bar: "from-teal-400 to-teal-700", text: "text-teal-600" };

const STATUS_STYLE = {
  rood: { cls: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
  geel: { cls: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  groen: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  niet_aangetroffen: { cls: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const BepalingKaart = ({ s }) => {
  const st = STATUS_STYLE[s.status] || STATUS_STYLE.niet_aangetroffen;
  return (
    <div data-testid={`contract-bepaling-${s.key}`} className="border border-slate-200 rounded-xl bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <span className="font-heading font-semibold text-slate-900">{s.label}</span>
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {s.status_label}
        </span>
      </div>
      <p className="text-sm text-slate-700">{s.toelichting}</p>
      {s.bron_voorrang ? <p className="mt-1 text-xs text-slate-500"><strong>Voorrang:</strong> {s.bron_voorrang}</p> : null}
      {s.wor_citaat?.tekst && (
        <div className="mt-3 bg-teal-50/60 border border-teal-100 rounded-lg p-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-teal-700 mb-1">Bron · {s.wor_citaat.artikel}</p>
          <p className="text-xs text-slate-600 leading-relaxed line-clamp-4">{s.wor_citaat.tekst}</p>
          <p className="text-[11px] text-slate-400 mt-1">{s.wor_citaat.bron} · {s.wor_citaat.versie}</p>
        </div>
      )}
      {s.fragment && (
        <details className="mt-2">
          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800">Toon contractfragment (PII gemaskeerd)</summary>
          <p className="mt-1.5 text-xs text-slate-600 bg-slate-50 rounded p-2 leading-relaxed">“{s.fragment}”</p>
        </details>
      )}
      <p className="mt-2 text-[11px] text-slate-400">{s.vertrouwensniveau}</p>
    </div>
  );
};

const Contractcheck = () => {
  const [tab, setTab] = useState("tekst");
  const [tekst, setTekst] = useState("");
  const [file, setFile] = useState(null);
  const [naam, setNaam] = useState("");
  const [bewaar, setBewaar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rapport, setRapport] = useState(null);
  const [docs, setDocs] = useState([]);
  const [exporting, setExporting] = useState(false);

  const loadDocs = () => api.get("/contract/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { loadDocs(); }, []);

  const controleer = async () => {
    setBusy(true); setRapport(null);
    try {
      let body = {};
      if (tab === "tekst") {
        if (!tekst.trim()) { toast.error("Plak eerst de contracttekst."); setBusy(false); return; }
        if (bewaar && naam.trim()) {
          const blob = new Blob([tekst], { type: "text/plain" });
          const fd = new FormData();
          fd.append("file", blob, `${naam.trim()}.txt`); fd.append("documentnaam", naam.trim());
          fd.append("versie", "1"); fd.append("bewaar", "true");
          const up = await api.post("/contract/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          body = { doc_id: up.data.doc_id, documentnaam: naam.trim() }; loadDocs();
        } else {
          body = { tekst, documentnaam: naam.trim() || "Geplakt contract" };
        }
      } else {
        if (!file) { toast.error("Kies een bestand."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file); fd.append("documentnaam", naam.trim() || file.name);
        fd.append("versie", "1"); fd.append("bewaar", bewaar ? "true" : "false");
        const up = await api.post("/contract/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        body = up.data.doc_id ? { doc_id: up.data.doc_id, documentnaam: naam.trim() || file.name }
                              : { tekst: up.data.tekst, documentnaam: naam.trim() || file.name };
        if (up.data.bewaard) loadDocs();
      }
      const res = await api.post("/contract/analyse", body);
      setRapport(res.data); toast.success("Contractcheck gereed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse mislukt.");
    } finally { setBusy(false); }
  };

  const controleerBewaard = async (doc) => {
    setBusy(true); setRapport(null);
    try {
      const res = await api.post("/contract/analyse", { doc_id: doc.id, documentnaam: doc.documentnaam });
      setRapport(res.data); toast.success("Contractcheck gereed.");
    } catch (e) { toast.error(e.response?.data?.detail || "Analyse mislukt."); }
    finally { setBusy(false); }
  };

  const verwijder = async (doc) => {
    if (!window.confirm(`'${doc.documentnaam}' verwijderen?`)) return;
    try { await api.delete(`/contract/documents/${doc.id}`); toast.success("Verwijderd."); loadDocs(); }
    catch { toast.error("Verwijderen mislukt."); }
  };

  const exporteerPdf = async () => {
    if (!rapport?.analyse_id) return;
    setExporting(true);
    try {
      const res = await api.get(`/contract/analyses/${rapport.analyse_id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Contractcheck_${(rapport.documentnaam || "rapport").replace(/[^\w-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF geëxporteerd.");
    } catch { toast.error("Exporteren mislukt."); }
    finally { setExporting(false); }
  };

  return (
    <div className="animate-fade-up" data-testid="contractcheck-page">
      <PageHeader title="Contractcheck" icon={FileCheck2} accent={ACCENT}
        subtitle="Controleer je eigen arbeidscontract tegen de CAO, het Burgerlijk Wetboek en de Arbeidstijdenwet. De app signaleert indicatief mogelijke afwijkingen, risico's en niet-aangetroffen bepalingen — altijd met bronverwijzing. Geen juridisch advies." />

      <div className="mb-4 flex items-start gap-2 text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-lg p-3" data-testid="contract-privacy-note">
        <Lock className="w-4 h-4 mt-0.5 shrink-0 text-teal-600" />
        <span>Privacy-by-default: je contract wordt <strong>niet opgeslagen</strong> tenzij je dat expliciet kiest. Gevoelige gegevens (BSN, IBAN) worden in de weergave gemaskeerd. Opgeslagen contracten zijn privé en niet zichtbaar voor anderen.</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex gap-2 mb-4">
              <button data-testid="contract-tab-tekst" onClick={() => setTab("tekst")} className={`text-sm font-medium px-3 py-1.5 rounded-lg ${tab === "tekst" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>Tekst plakken</button>
              <button data-testid="contract-tab-bestand" onClick={() => setTab("bestand")} className={`text-sm font-medium px-3 py-1.5 rounded-lg ${tab === "bestand" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-600"}`}>Bestand uploaden</button>
            </div>
            {tab === "tekst" ? (
              <Textarea data-testid="contract-tekst-input" value={tekst} onChange={(e) => setTekst(e.target.value)} rows={9}
                placeholder="Plak hier de tekst van je arbeidscontract…" className="resize-none" />
            ) : (
              <div>
                <Label className="text-sm">Bestand (PDF, DOCX of TXT)</Label>
                <Input data-testid="contract-file-input" type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} />
              </div>
            )}
            <div className="mt-4 grid sm:grid-cols-2 gap-3 items-end">
              <div><Label className="text-sm">Documentnaam</Label>
                <Input data-testid="contract-naam-input" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Arbeidsovereenkomst 2026" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input data-testid="contract-bewaar" type="checkbox" checked={bewaar} onChange={(e) => setBewaar(e.target.checked)} className="rounded border-slate-300" />
                Opslaan voor later gebruik (privé)
              </label>
            </div>
            <Button data-testid="contract-controleer-btn" onClick={controleer} disabled={busy} className="mt-4 bg-teal-600 hover:bg-teal-700">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}
              {busy ? "Controleren…" : "Controleer contract"}
            </Button>
          </div>

          {rapport && (
            <div data-testid="contract-rapport" className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <h2 className="font-heading text-lg font-bold text-slate-900">Resultaat{rapport.documentnaam ? `: ${rapport.documentnaam}` : ""}</h2>
                  <Button data-testid="contract-export-pdf-btn" onClick={exporteerPdf} disabled={exporting} size="sm" variant="outline" className="h-8 border-slate-300 text-slate-700">
                    {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Exporteer als PDF
                  </Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-lg px-3 py-2 bg-red-50" data-testid="contract-sum-rood">
                    <p className="text-[11px] text-red-700">Mogelijke afwijking/risico</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.rood}</p></div>
                  <div className="rounded-lg px-3 py-2 bg-amber-50" data-testid="contract-sum-geel">
                    <p className="text-[11px] text-amber-700">Aandachtspunt</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.geel}</p></div>
                  <div className="rounded-lg px-3 py-2 bg-emerald-50" data-testid="contract-sum-groen">
                    <p className="text-[11px] text-emerald-700">Geen afwijking</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.groen}</p></div>
                  <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="contract-sum-niet">
                    <p className="text-[11px] text-slate-500">Niet aangetroffen</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.niet_aangetroffen}</p></div>
                </div>
                {rapport.bronprioriteit && (
                  <p className="mt-3 text-xs text-slate-500"><strong>Bronprioriteit bij tegenstrijdigheid:</strong> {rapport.bronprioriteit.join(" → ")}</p>
                )}
                <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2.5">
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {rapport.disclaimer}
                </div>
              </div>
              <div className="space-y-3">
                {rapport.signalen.map((s) => <BepalingKaart key={s.key} s={s} />)}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-1"><ScrollText className="w-4 h-4 text-teal-600" />
              <h3 className="font-heading font-bold text-slate-900">Mijn contracten</h3></div>
            <p className="text-xs text-slate-500 mb-3">Privé opgeslagen. Niet zichtbaar voor anderen en niet toegevoegd aan de kennisbank.</p>
            {docs.length === 0 ? (
              <p data-testid="contract-geen-docs" className="text-xs text-slate-400">Nog geen opgeslagen contracten.</p>
            ) : (
              <ul className="space-y-2" data-testid="contract-docs-lijst">
                {docs.map((g) => g.versies.map((d) => (
                  <li key={d.id} data-testid={`contract-doc-${d.id}`} className="border border-slate-200 rounded-lg p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />{d.documentnaam}</p>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(d.created_at)} · v{d.versie}</p>
                      </div>
                      <button data-testid={`contract-doc-del-${d.id}`} onClick={() => verwijder(d)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Button data-testid={`contract-doc-analyse-${d.id}`} onClick={() => controleerBewaard(d)} disabled={busy} size="sm" variant="outline" className="mt-2 h-7 w-full border-teal-200 text-teal-700 hover:bg-teal-50">
                      Opnieuw controleren
                    </Button>
                  </li>
                )))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contractcheck;
